import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  PermissionManagementMatrixModel,
  PermissionModel,
  UserGroupModel,
  UserModel,
} from "services/api/cplauthapi/CPLAuthAPI";
import { IPermissionsService } from "services/PermissionsService";
import { IUserGroupsService } from "services/UserGroupsService";
import { IUsersService } from "services/UsersService";
import { RootState } from "setup";
import { container } from "setup/dependency/inversify.config";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";

const initialState = {
  Permissions: new Array<PermissionModel>(),
  PermissionAssignments: new PermissionManagementMatrixModel(),
  Users: new Array<UserModel>(),
  UserGroups: new Array<UserGroupModel>(),
  LoadingUsers: false,
  LoadingUserGroups: false,
  LoadingPermissions: false,
  LoadingPermissionAssignments: false,
};

export const permissionAssignmentsSlice = createSlice({
  name: "permissionAssignments",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllUsers.pending, (state) => {
        state.LoadingUsers = true;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.Users = action.payload;
        state.LoadingUsers = false;
      })
      .addCase(fetchAllUsers.rejected, (state) => {
        state.Users = new Array<UserModel>();
        state.LoadingUsers = false;
      })
      .addCase(fetchAllUserGroups.pending, (state) => {
        state.LoadingUserGroups = true;
      })
      .addCase(fetchAllUserGroups.fulfilled, (state, action) => {
        state.UserGroups = action.payload;
        state.LoadingUserGroups = false;
      })
      .addCase(fetchAllUserGroups.rejected, (state) => {
        state.UserGroups = new Array<UserGroupModel>();
        state.LoadingUserGroups = false;
      })
      .addCase(fetchPermissionAssignmentsMatrix.pending, (state) => {
        state.LoadingPermissionAssignments = true;
      })
      .addCase(fetchPermissionAssignmentsMatrix.fulfilled, (state, action) => {
        state.PermissionAssignments = action.payload;
        state.LoadingPermissionAssignments = false;
      })
      .addCase(fetchPermissionAssignmentsMatrix.rejected, (state) => {
        state.PermissionAssignments = new PermissionManagementMatrixModel();
        state.LoadingPermissionAssignments = false;
      })
      .addCase(fetchAllPermissionsForCategory.pending, (state) => {
        state.LoadingPermissions = true;
      })
      .addCase(fetchAllPermissionsForCategory.fulfilled, (state, action) => {
        state.Permissions = action.payload;
        state.LoadingPermissions = false;
      })
      .addCase(fetchAllPermissionsForCategory.rejected, (state) => {
        state.Permissions = new Array<PermissionModel>();
        state.LoadingPermissions = false;
      });
  },
});

export const deletePermissionAssignment = createAsyncThunk<void, number, { state: RootState }>(
  "PermissionAssignments/DeletePermissionAssignment",
  async (permissionAssignmentId: number, thunkAPI) => {
    const permissionsService = container.get<IPermissionsService>(SERVICE_TYPES.PermissionService);
    await permissionsService.deletePermissionAssignment(permissionAssignmentId);
    const state = thunkAPI.getState();
    thunkAPI.dispatch(
      fetchPermissionAssignmentsMatrix(state.permissionCategories.SelectedPermissionCategory.permissionCategoryID!)
    );
  }
);

export const updateUserPermissionAssignment = createAsyncThunk<
  void,
  { userID: string; permissionID: number; grant: boolean },
  { state: RootState }
>("PermissionAssignments/UpdateUserPermissionAssignment", async ({ userID, permissionID, grant }, thunkAPI) => {
  const permissionsService = container.get<IPermissionsService>(SERVICE_TYPES.PermissionService);
  if (grant) {
    await permissionsService.grantUserPermissionAssignment(userID, permissionID);
  } else {
    await permissionsService.denyUserPermissionAssignment(userID, permissionID);
  }
  const state = thunkAPI.getState();
  thunkAPI.dispatch(
    fetchPermissionAssignmentsMatrix(state.permissionCategories.SelectedPermissionCategory.permissionCategoryID!)
  );
});

export const updateUserGroupPermissionAssignment = createAsyncThunk<
  void,
  { userGroupID: number; permissionID: number; grant: boolean },
  { state: RootState }
>(
  "PermissionAssignments/UpdateUserGroupPermissionAssignment",
  async ({ userGroupID, permissionID, grant }, thunkAPI) => {
    const permissionsService = container.get<IPermissionsService>(SERVICE_TYPES.PermissionService);
    if (grant) {
      await permissionsService.grantUserGroupPermissionAssignment(userGroupID, permissionID);
    } else {
      await permissionsService.denyUserGroupPermissionAssignment(userGroupID, permissionID);
    }
    const state = thunkAPI.getState();
    thunkAPI.dispatch(
      fetchPermissionAssignmentsMatrix(state.permissionCategories.SelectedPermissionCategory.permissionCategoryID!)
    );
  }
);

export const fetchAllPermissionsForCategory = createAsyncThunk(
  "PermissionAssignments/FetchAllPermissionsForCategory",
  async (permissionCategoryID: number) => {
    const permissionsService = container.get<IPermissionsService>(SERVICE_TYPES.PermissionService);
    return await permissionsService.getAllPermissionsForCategory(permissionCategoryID);
  }
);

export const fetchPermissionAssignmentsMatrix = createAsyncThunk(
  "PermissionAssignments/FetchPermissionAssignmentsMatrix",
  async (permissionCategoryID: number) => {
    const permissionsService = container.get<IPermissionsService>(SERVICE_TYPES.PermissionService);
    return await permissionsService.getPermissionAssignmentsMatrix(permissionCategoryID);
  }
);

export const fetchAllUsers = createAsyncThunk("PermissionAssignments/FetchAllUsers", async () => {
  const usersService = container.get<IUsersService>(SERVICE_TYPES.UsersService);
  return await usersService.getAllUsers(false);
});

export const fetchAllUserGroups = createAsyncThunk("PermissionAssignments/FetchAllUserGroups", async () => {
  const groupsService = container.get<IUserGroupsService>(SERVICE_TYPES.UserGroupsService);
  return await groupsService.getAllUserGroups();
});

export const selectPermissionAssignments = (state: RootState) => {
  return state.permissionAssignments.PermissionAssignments;
};

export const selectPermissions = (state: RootState) => {
  return state.permissionAssignments.Permissions;
};
export const selectAllUsers = (state: RootState) => {
  return state.permissionAssignments.Users;
};
export const selectAllGroups = (state: RootState) => {
  return state.permissionAssignments.UserGroups;
};
export const selectLoadingUsers = (state: RootState) => {
  return state.permissionAssignments.LoadingUsers;
};
export const selectLoadingUserGroups = (state: RootState) => {
  return state.permissionAssignments.LoadingUserGroups;
};
export const selectLoadingPermissions = (state: RootState) => {
  return state.permissionAssignments.LoadingPermissions;
};

export default permissionAssignmentsSlice.reducer;
